import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { BrandLockup } from "@/components/Logo";
import { api } from "@/lib/auth";
import { useClientAuth, formatApiError, startClientGoogle } from "@/lib/clientAuth";

export function AuthShell({ title, kicker, children, testid }) {
  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-obsidian-900 px-6 py-24" data-testid={testid}>
      <div className="pointer-events-none absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 50% 24%, rgba(212,175,55,0.14) 0%, transparent 60%)" }} />
      <div className="gold-frame relative w-full max-w-md rounded-3xl bg-obsidian-800 p-8 sm:p-10">
        <Link to="/"><BrandLockup className="mx-auto h-16 w-auto" /></Link>
        <span className="mt-6 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-gold">
          <Lock className="h-3 w-3" /> {kicker}
        </span>
        <h1 className="mt-3 text-center font-display text-3xl font-bold text-ivory">{title}</h1>
        <div className="mt-8">{children}</div>
      </div>
    </main>
  );
}

const inputCls = "w-full rounded-xl border border-gold/20 bg-obsidian-900 px-4 py-3 text-sm text-ivory outline-none transition focus:border-gold/60";
const btnCls = "w-full rounded-full bg-gold px-6 py-3 font-sans text-sm font-semibold uppercase tracking-[0.12em] text-obsidian-900 transition hover:shadow-[0_0_28px_rgba(212,175,55,0.35)] disabled:opacity-50";

export default function ClientLogin({ corporate = false }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useClientAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/client/auth/login", { email, password });
      setUser(data);
      toast.success("Signed in");
      nav("/portal");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell kicker={corporate ? "Corporate Portal" : "Client Portal"} title="Sign In" testid={corporate ? "page-corporate-login" : "page-client-login"}>
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="login-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
        <input data-testid="login-password" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
        <div className="text-right">
          <Link to="/forgot-password" data-testid="forgot-link" className="text-xs text-gold/80 hover:text-gold">Forgot password?</Link>
        </div>
        <button data-testid="login-submit" disabled={loading} className={btnCls}>{loading ? "Signing in…" : "Sign In"}</button>
      </form>
      <div className="my-5 flex items-center gap-3 text-ivory/30"><div className="h-px flex-1 bg-gold/15" /><span className="text-[10px] uppercase tracking-widest">or</span><div className="h-px flex-1 bg-gold/15" /></div>
      <button data-testid="login-google" onClick={startClientGoogle} className="w-full rounded-full border border-gold/30 px-6 py-3 text-sm font-semibold text-ivory transition hover:bg-gold/10">
        Continue with Google
      </button>
      <p className="mt-6 text-center text-sm text-ivory/60">
        New here? <Link to="/register" data-testid="to-register" className="text-gold hover:underline">Create an account</Link>
      </p>
      {corporate && (
        <p className="mt-2 text-center text-xs text-ivory/45">Corporate accounts require approval by Nanak Chauffeurs after sign-up.</p>
      )}
    </AuthShell>
  );
}
